<template>
    <div class="col-sm-12 nopads mb-3">
        <h2 class="editor-header">
            {{ contract && contract.id > 0 ? contract.name : $t('contracts.add') }}
        </h2>
        <div
            v-if="contract"
            v-show="!order && !taskType && !costItem"
            class="col-12"
        >
            <div>
                <ul class="nav nav-tabs routa-tabs">
                    <li class="nav-item">
                        <button
                            class="routa-tab"
                            :class="{ active: tabIndex === 0 }"
                            @click="tabIndex = 0"
                        >
                            <font-awesome-icon
                                v-if="!hasTaskTypes && contract.is_contract_party"
                                id="basicTitle"
                                icon="exclamation-circle"
                                class="defect me-2"
                                :title="$t('contracts.warning_task_types')"
                            />
                            {{ $t('contracts.basic_info') }}
                        </button>
                    </li>
                    <li class="nav-item">
                        <button
                            class="routa-tab"
                            :class="{ active: tabIndex === 1 }"
                            @click="tabIndex = 1"
                        >
                            <font-awesome-icon
                                v-if="!hasActiveOrders && contract.is_contract_party"
                                id="basicTitle"
                                icon="exclamation-circle"
                                class="defect me-2"
                                :title="$t('contracts.no_ongoing_orders')"
                            />
                            {{ $t('observations.orders') }}
                        </button>
                    </li>
                    <li
                        v-if="contract.is_contract_party"
                        class="nav-item"
                    >
                        <button
                            class="routa-tab"
                            :class="{ active: tabIndex === 2 }"
                            @click="tabIndex = 2"
                        >
                            {{ $t('contracts.schedule') }}
                        </button>
                    </li>
                    <li
                        v-if="contract.is_contract_party"
                        class="nav-item"
                    >
                        <button
                            class="routa-tab"
                            :class="{ active: tabIndex === 3 }"
                            @click="tabIndex = 3"
                        >
                            {{ $t('contracts.docs') }}
                            <font-awesome-icon
                                v-if="!contract.is_contract_party"
                                icon="lock"
                            />
                        </button>
                    </li>
                    <li
                        v-if="contract.is_contract_party"
                        class="nav-item"
                    >
                        <button
                            class="routa-tab"
                            :class="{ active: tabIndex === 4 }"
                            @click="tabIndex = 4"
                        >
                            {{ $t('contracts.reports') }}
                            <font-awesome-icon
                                v-if="!contract.is_contract_party"
                                icon="lock"
                            />
                        </button>
                    </li>
                    <li
                        v-if="contract.ely_contract && contract.is_contract_party"
                        class="nav-item"
                    >
                        <button
                            class="routa-tab"
                            :class="{ active: tabIndex === 5 }"
                            @click="tabIndex = 5"
                        >
                            {{ $t('contracts.ely_reports') }}
                        </button>
                    </li>
                    <li
                        v-if="contract.ely_contract"
                        class="nav-item"
                    >
                        <button
                            class="routa-tab"
                            :class="{ active: tabIndex === 6 }"
                            @click="tabIndex = 6"
                        >
                            {{ $t('harja.harja_messages') }}
                        </button>
                    </li>
                </ul>
                <!-- Tab Content -->
                <div class="tab-content routa-tab-content">
                    <div
                        class="tab-pane fade"
                        :class="{ show: tabIndex === 0, active: tabIndex === 0 }"
                        style="min-height: 75vh"
                    >
                        <contract-editor
                            v-if="contract && contract.is_contract_party"
                            ref="contractEditor"
                            v-model="contract"
                            class="px-2"
                            :user="user"
                            :contract-trip-list="contractTripList"
                            @close="emitCloseAndUpdate"
                            @close-and-update="closeAndUpdate"
                            @add-task-type="showTaskTypeEditor"
                        />
                        <contract-simple-info
                            v-else-if="contract"
                            :contract="contract"
                        />
                    </div>
                    <div
                        class="tab-pane fade"
                        :class="{ show: tabIndex === 1, active: tabIndex === 1 }"
                        style="min-height: 75vh"
                    >
                        <contract-orders
                            v-if="contract && contract.id > 0"
                            ref="contractOrders"
                            :contract="contract"
                            :order-filter="orderFilter"
                            :tiera-api-key-allowed="tieraApiKeyAllowed"
                            :task-type-filter="taskTypeFilter"
                            :executor-filter="executorFilter"
                            @set-order-filter="setOrderFilter"
                            @set-task-type-filter="setTaskTypeFilter"
                            @set-executor-filter="setExecutorFilter"
                            @add-order="addOrder"
                            @edit-order="editOrder"
                            @orders-updated="onOrdersUpdated"
                        />
                    </div>
                    <div
                        class="tab-pane fade"
                        :class="{ show: tabIndex === 2, active: tabIndex === 2 }"
                        style="min-height: 75vh"
                    >
                        <contract-calendar
                            v-if="contract && contract.id > 0"
                            ref="calendar"
                            :contract="contract"
                            :auto-expand="true"
                            :user="user"
                            :disable-contract-actions="true"
                            @order-updated="onCalendarOrderUpdated"
                            @select-duty-officer-user="onSelectDutyOfficerUser"
                            @duty-officer-updated="onDutyOfficerUpdated"
                        />
                        <hr>
                        <duty-officer-plan
                            ref="dutyOfficerPlan"
                            :contract-id="contract.id"
                            :selected-schedule="selectedDutyOfficerSchedule"
                            @update-calendar="updateCalendar"
                        />
                    </div>

                    <div
                        class="tab-pane fade"
                        :class="{ show: tabIndex === 3, active: tabIndex === 3 }"
                        style="min-height: 75vh"
                    >
                        <contract-documents
                            v-if="contract && contract.id > 0"
                            v-model="contract"
                        />
                    </div>

                    <div
                        class="tab-pane fade"
                        :class="{ show: tabIndex === 4, active: tabIndex === 4 }"
                        style="min-height: 75vh"
                    >
                        <contract-reports
                            v-if="contract && contract.id > 0"
                            :contract="contract"
                            :show-title="false"
                            :is-observer="isObserver"
                            :user="user"
                        />
                    </div>

                    <div
                        class="tab-pane fade"
                        :class="{ show: tabIndex === 5, active: tabIndex === 5}"
                        style="min-height: 75vh"
                    >
                        <contract-ely-reports
                            v-if="contract && contract.id"
                            :contract="contract"
                            :user="user"
                        />
                    </div>

                    <div
                        class="tab-pane fade"
                        :class="{ show: tabIndex === 6, active: tabIndex === 6}"
                        style="min-height: 75vh"
                    >
                        <harja-messages
                            v-if="tabIndex === 6"
                            :contract-id="contract.id"
                        />
                    </div>
                </div>
            </div>
        </div>

        <base-modal
            v-if="order"
            size="lg"
            hide-footer
            hide-header
            @close="order = null"
        >
            <order-editor
                v-if="order"
                ref="editor"
                :contract="contract"
                :order="order"
                :user="user"
                :is-observer="isObserver"
                @close="closeOrderEditor"
                @close-and-update="closeOrderAndUpdate"
            />
        </base-modal>

        <base-modal
            v-if="taskType"
            size="lg"
            hide-footer
            hide-header
            @close="taskType = null"
        >
            <task-type-editor
                :task-type="newTaskType()"
                @close="hideTaskTypeEditor"
                @close-and-update="hideTaskTypeEditor"
            />
        </base-modal>

        <base-modal
            v-if="costItem"
            size="lg"
            hide-footer
            hide-header
            @close="costItem = null"
        >
            <additional-cost-editor
                :additional-cost="costItem"
                @close-and-update="updateCosts"
                @close="costItem = null"
            />
        </base-modal>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
        <div
            v-if="!showTitle"
            class="col-12 d-flex justify-content-end"
        >
            <hr>
            <button
                class="form-button btn btn-sm btn-primary"
                @click="closeAndUpdate"
            >
                {{ $t('common.back') }}
            </button>
        </div>
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {orderStatus, workManagementHelper} from "@/components/mixins/WorkManagementMixin";
import OrderEditor from '../order/OrderEditor'
import TaskTypeEditor from '../tasktype/TaskTypeEditor'
import ContractOrders from "@/components/contract/ContractOrders";
import ContractDocuments from "@/components/contract/ContractDocuments";
import ContractEditor from "@/components/contract/ContractEditor";
import ContractCalendar from "@/components/contractcalendar/ContractCalendar";
import ContractReports from "@/components/contractreport/ContractReports";
import ContractSimpleInfo from "@/components/contract/ContractSimpleInfo";
import AdditionalCostEditor from "@/components/additionalcost/AdditionalCostEditor";
import ContractElyReports from "@/components/contract/external/ContractElyReports";
import HarjaMessages from "@/components/harja/HarjaMessages.vue";
import DutyOfficerPlan from "@/components/contract/DutyOfficerPlan.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import BaseModal from "@/components/BaseModal";

export default {
    name: 'ContractDetails',
    components: {
        BaseModal,
        FontAwesomeIcon,
        DutyOfficerPlan,
        HarjaMessages,
        ContractElyReports,
        AdditionalCostEditor,
        ContractSimpleInfo,
        ContractReports,
        ContractCalendar, ContractEditor, ContractDocuments, ContractOrders, OrderEditor, TaskTypeEditor},
    mixins: [restApi, workManagementHelper, orderStatus],
    props: {
        modelValue: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        contractTripList: {
            type: Boolean,
            default: false
        },
        isObserver: {
            type: Boolean,
            default: false
        },
        activeOrderEditor: Boolean,
        showTitle: {
            type: Boolean,
            default: true
        }
    },
    data: function () {
        return {
            loading: false,
            contract: null,
            selectedDutyOfficerSchedule: null,
            orders: [],
            ordersToAdd: [],
            order: null,
            closeOnCancel: false,
            orderFilter: [],
            taskTypeFilter: [],
            executorFilter: [],
            addMessage: '',
            taskType: false,
            tabIndex: 0,
            costItem: null,
            hasActiveOrders: true,
            tieraApiKeyAllowed: false
        }
    },
    computed: {
        hasTaskTypes() {
            return this.contract && this.contract.task_types && this.contract.task_types.length > 0
        },
        tabs() {
            return [
                {
                    title: this.$t('contracts.basic_info'),
                    icon: 'exclamation-circle',
                    iconClass: 'defect',
                    tooltip: this.$t('contracts.warning_task_types'),
                },
                {
                    title: this.$t('menu.orders'),
                    icon: 'exclamation-circle',
                    iconClass: 'defect',
                    tooltip: this.$t('contracts.no_ongoing_orders'),
                },
                {
                    title: this.$t('contracts.schedule')
                },
                {
                    title: this.$t('contracts.docs'),
                    icon: 'lock',
                },
                {
                    title: this.$t('contracts.reports'),
                    icon: 'lock',
                },
                {
                    title: this.$t('contracts.ely_reports')
                },
                {
                    title: this.$t('harja.harja_messages')
                }
            ];
        }
    },
    watch: {
        contract(val) {
            this.$emit('input', val)
            this.updateActiveOrders()
        },
        order: function() {
            this.order ? this.$emit('orderActiveChange', true) : this.$emit('orderActiveChange', false)
        },
        activeOrderEditor: function () {
            if(!this.activeOrderEditor) this.closeOrderEditor()
        }
    },
    mounted: function () {
        this.contract = this.modelValue
        this.orderFilter=[orderStatus.OPEN, orderStatus.PLAN, orderStatus.IN_PROGRESS, orderStatus.STORED, orderStatus.COMPLETED]
        this.updateActiveOrders()
        this.checkTieraApiKeyAccess()
    },
    methods: {
        updateCalendar() {
            this.$refs.calendar.refreshDutyOfficer()
        },
        onSelectDutyOfficerUser(payload) {
            // payload: { user, schedules } — forwarded from the calendar when a user row is clicked.
            // Pass it to the duty officer plan form so it loads that user for editing.
            this.selectedDutyOfficerSchedule = payload
        },
        onDutyOfficerUpdated() {
            // Calendar changed a schedule (drag/resize) -> re-sync the form's loaded user
            if (this.$refs.dutyOfficerPlan) {
                this.$refs.dutyOfficerPlan.refreshCurrentUser()
            }
        },
        editOrder: function (order) {
            this.order = order
        },
        closeAndUpdate: function (data) {
            if (this.modelValue.id < 1 && data.id > 0) {
                // If we just added a contract successfully, ask to create orders as well
                this.contract = data
                this.addOpenOrders()
                this.closeOnCancel = true
                this.updateActiveOrders()
            }
            if (this.contractTripList) {
                this.emitCloseAndUpdate()
            }
        },
        emitCloseAndUpdate() {
            this.$emit('closeAndUpdate', this.contract)
            this.updateActiveOrders()
        },
        addOrder: function () {
            this.order = this.createEditorOrder()
            this.$emit('scrollTop')
        },
        closeOrderEditor: function () {
            this.order = null
        },
        closeOrderAndUpdate: function () {
            this.closeOrderEditor()
            this.$nextTick(() => {
                this.$refs.contractOrders.fetchContractOrders()
                if(this.$refs.calendar) {
                    this.$refs.calendar.refresh()
                }
            })
        },
        setOrderFilter(orderFilter) {
            this.orderFilter = orderFilter
        },
        setTaskTypeFilter(taskTypeFilter) {
            this.taskTypeFilter = taskTypeFilter
        },
        setExecutorFilter(executorFilter) {
            this.executorFilter = executorFilter
        },
        showTaskTypeEditor: function () {
            this.taskType = true
            this.$emit('scrollTop')
        },

        hideTaskTypeEditor: function () {
            this.taskType = false
            this.$nextTick(() => this.$refs.contractEditor.refreshTaskTypes())
        },
        editCost(costItem) {
            this.costItem = costItem
        },
        updateCosts() {
            this.costItem = null
            this.$refs.costs.updateCosts()
        },
        onOrdersUpdated() {
            // Refresh non-visible tab
            if (this.tabIndex === 1) {
                this.$refs.calendar.refresh()
            }
            this.updateActiveOrders()
        },
        onCalendarOrderUpdated() {
            // Refresh non-visible tab
            this.$refs.contractOrders.fetchContractOrders()
        },
        updateActiveOrders() {
            this.hasActiveOrders = this.contract && this.contract.id < 0 || this.$refs.contractOrders && this.$refs.contractOrders.hasActiveOrders()
        },
        checkTieraApiKeyAccess() {
            // TODO maybe put apikey types to some config
            this.restFetchParams(this.apiUrl, {type: 9}, this.handleTieraApiKeySuccess, this.handleTieraApiKeyFail)
        },

        handleTieraApiKeySuccess() {
            this.tieraApiKeyAllowed = true
        },

        handleTieraApiKeyFail() {
            this.tieraApiKeyAllowed = false
        },

        showTab(index) {
            this.tabIndex = index
        }
    }
}
</script>
